import { FC, ReactNode } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { IRoutingConfigVersion } from '@entities/routing/config-version';
import ModalConfirm from '@features/modal-confirm';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useModal } from '@shared/components/Modal';

import VersionListModal from './VersionListModal';

interface IProps {
  viewingVersion: IRoutingConfigVersion;
  // Version data arrives from the widget, which owns all three queries so the
  // page shows a single spinner until everything is ready.
  versions: IRoutingConfigVersion[];
  isVersionsError: boolean;
  activeVersion: IRoutingConfigVersion | null;
  // True when the active-version request failed with a non-404 — distinct from
  // "no active config exists" (a 404), which arrives as activeVersion: null.
  isActiveUnavailable?: boolean;
  // Edit mode sets this; a dirty view demands confirmation before switching away.
  isDirty?: boolean;
  // While a Save Draft request is in flight, switching is blocked so a completing
  // save cannot land after the operator has moved to another version.
  isSaving?: boolean;
  // Runs once the operator confirms losing the edits, before the switch happens.
  onDiscardConfirmed?: () => void;
  // Version-level actions (Edit Config, Publish) rendered after the bar's own buttons.
  actions?: ReactNode;
}

// The active-config bar: which version runs on prod, which one is on screen, and
// the entry points into switching (view-only picker modal) and History. A version
// becomes active via Publish/Activate, never from here.
const VersionSwitcher: FC<IProps> = ({
  viewingVersion,
  versions,
  isVersionsError,
  activeVersion,
  isActiveUnavailable = false,
  isDirty = false,
  isSaving = false,
  onDiscardConfirmed,
  actions,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();

  const applySwitch = (id: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('version', String(id));
    setSearchParams(next);
  };

  const handleView = (id: number) => {
    if (isSaving) return;
    if (!isDirty) {
      closeModal();
      applySwitch(id);
      return;
    }

    openModal(
      <ModalConfirm
        title='Discard unsaved changes?'
        message={`Switching to v${id} will discard your unsaved edits — they will be lost.`}
        onConfirm={() => {
          onDiscardConfirmed?.();
          closeModal(); // the confirm
          closeModal(); // the picker under it
          applySwitch(id);
        }}
        onCancel={closeModal}
      />,
      'sm',
    );
  };

  const openPicker = () => {
    // A deep-linked version can fall outside the fetched page (an old archived id);
    // keep it listed so the picker always shows the version that is on screen.
    const listed = versions.some(version => version.id === viewingVersion.id)
      ? versions
      : [...versions, viewingVersion];

    openModal(
      <VersionListModal
        versions={listed}
        isError={isVersionsError}
        viewingId={viewingVersion.id}
        onView={handleView}
      />,
      'md',
    );
  };

  const isViewingActive = activeVersion !== null && viewingVersion.id === activeVersion.id;

  return (
    <section className='routing-version-switcher'>
      {/* The bar states only what is live on prod; the metadata card below is the
          viewed version and carries the amber "not the active version" marker when
          the two differ — no fact is repeated */}
      <div className='routing-version-switcher-slot'>
        <CheckCircleOutlineIcon className='routing-version-switcher-icon' />
        <div className='routing-version-switcher-info'>
          {activeVersion === null ? (
            <div className='routing-version-switcher-heading'>
              {isActiveUnavailable ? (
                <span className='routing-version-switcher-unavailable'>
                  Active version is unavailable.
                </span>
              ) : (
                'No active routing config yet.'
              )}
            </div>
          ) : isViewingActive ? (
            // The card below already details this exact version — a confirmation
            // line is all the bar needs.
            <div className='routing-version-switcher-live'>You are viewing the live version.</div>
          ) : (
            <>
              <div className='routing-version-switcher-slot-label'>Active on prod</div>
              <div className='routing-version-switcher-heading'>
                <span className='routing-version-number'>v{activeVersion.id}</span>
                <span className='routing-version-switcher-name'>{activeVersion.name}</span>
              </div>
              <div className='routing-version-switcher-meta'>
                {activeVersion.published_at && (
                  <span className='routing-version-switcher-date'>
                    {new Date(activeVersion.published_at).toLocaleString()}
                  </span>
                )}
                {activeVersion.comment && (
                  <span className='routing-version-switcher-comment'>
                    {activeVersion.comment}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className='routing-version-switcher-actions'>
        <button
          type='button'
          className='routing-version-switcher-btn'
          onClick={openPicker}
          disabled={isSaving}
        >
          <SwapHorizIcon fontSize='small' /> Switch Config
        </button>
        <button
          type='button'
          className='routing-version-switcher-btn'
          onClick={() => navigate('/routing/history')}
        >
          <HistoryIcon fontSize='small' /> History
        </button>
        {actions}
      </div>
    </section>
  );
};

export default VersionSwitcher;
